import { Component, Input } from '@angular/core';
import { DefaultLabelFormatterCallbackParams, TooltipComponentFormatterCallbackParams } from 'echarts';
import { StringNumberMap } from '@bn/shared/entities/models';
import { MetricFormatPipe } from '@bn/shared/entities/pipes';
import { DAYJS_FORMAT, DateTimeUtils } from '@bn/shared/utils';
import { TypedChartAbstractDirective } from '@bn/web-ui/chart/components/abstracts/typed-chart-abstract.directive';
import { ChartComponent } from '@bn/web-ui/chart/components/base/chart/chart.component';
import { ShieldOverviewBreakdownData, ShieldOverviewBreakdownEntry } from './shield-breakdown.model';

@Component({
  selector: 'app-chart-volume-of-attacks',
  templateUrl: './chart-volume-of-attacks.component.html',
  imports: [ChartComponent],
})
export class ChartVolumeOfAttacksComponent extends TypedChartAbstractDirective {
  @Input()
  public set summaryLabels(summaryLabelsData: StringNumberMap | undefined) {
    if (summaryLabelsData) this.setSummaryLabels(summaryLabelsData);
  }

  breakdownData?: ShieldOverviewBreakdownData;
  @Input()
  public set inputBreakdownData(value: ShieldOverviewBreakdownData | undefined) {
    this.breakdownData = value;
    if (this.chartSeriesData) this.setChartOptions(this.chartSeriesData as (string | number)[][]);
  }

  protected readonly tooltipOption = {
    valueFormatter: (value: unknown) => this.namingScaleFormatPipe.transform(Number(value || 0), 'metricSymbol', this.unit, undefined, 2).formatted,
  };

  constructor() {
    super();
  }

  protected setChartOptions(chartSeriesData: (string | number)[][]): void {
    const baseTooltip = this.getTooltipChartOption();

    this.chartOptions = {
      color: [
        '#E1F2FF',
        this.chartThemeVars?.contextColors.info || '#0090FF',
        this.chartThemeVars?.contextColors.success || '#00C48C',
        this.chartThemeVars?.contextColors.danger || '#FF4D4F',
        this.chartThemeVars?.contextColors['light-danger'] || '#FFB4A9',
        '#63A1FB',
        this.chartThemeVars?.contextColors.warning || '#FFA940',
      ],
      legend: {
        show: false,
      },
      yAxis: {
        minInterval: 0.25,
        axisLabel: {
          formatter: (value: number) => this.namingScaleFormatPipe.transform(value, 'metricSymbol', this.unit, undefined, 2).formatted,
        },
      },
      dataset: {
        source: chartSeriesData,
      },
      tooltip: {
        ...baseTooltip,
        formatter: (params: TooltipComponentFormatterCallbackParams) => this.formatBreakdownTooltip(params),
      },
    };
  }

  private formatBreakdownTooltip(params: TooltipComponentFormatterCallbackParams): string {
    if (!Array.isArray(params) || params.length === 0) return '';

    const firstParam = params[0] as DefaultLabelFormatterCallbackParams & { axisValue?: string | number };
    const rawTimestamp = firstParam.axisValue ?? firstParam.name;
    const breakdown = this.findClosestBreakdown(rawTimestamp);

    const dateTime = DateTimeUtils.toLocalTime(rawTimestamp);
    const formattedDate = dateTime.isValid() ? dateTime.format(DAYJS_FORMAT.ChartTooltipDateTime) : String(rawTimestamp);

    const rows = params.map((param) => this.formatTooltipRow(param, breakdown)).join('');

    return `<div style="font-weight: 600; margin-bottom: 8px">${formattedDate}</div>${rows}`;
  }

  private formatTooltipRow(param: DefaultLabelFormatterCallbackParams, breakdown?: ShieldOverviewBreakdownEntry): string {
    const featureConfig: Record<string, { key: keyof ShieldOverviewBreakdownEntry; breakdownKeys: string[] }> = {
      WAF: { key: 'waf', breakdownKeys: ['blocked', 'challenged', 'logged'] },
      'DDoS mitigation': { key: 'ddos', breakdownKeys: ['blocked', 'challenged', 'logged', 'verified'] },
      'Rate limiting': { key: 'rateLimit', breakdownKeys: ['blocked', 'challenged', 'logged'] },
      'Bot detection': { key: 'botDetection', breakdownKeys: ['challenged', 'logged'] },
      'Access lists': { key: 'accessLists', breakdownKeys: ['blocked', 'challenged'] },
      'Upload scanning': { key: 'uploadScanning', breakdownKeys: ['blocked', 'logged'] },
      'API Guardian': { key: 'apiGuardian', breakdownKeys: ['blocked', 'logged'] },
    };

    const valueArray = param.value as (string | number)[];
    const total = this.namingScaleFormatPipe.transform(Number(valueArray?.[param.seriesIndex! + 1] || 0), 'metricSymbol', this.unit, undefined, 2).formatted;

    const config = featureConfig[param.seriesName ?? ''];
    const breakdownHtml = this.formatBreakdownDetails(breakdown, config);

    return `
      <div>
        <div style="display: flex; align-items: center; gap: 6px">
          ${param.marker}
          <span style="font-weight: 500">${param.seriesName}:</span>
          <span>${total}</span>
        </div>
        ${breakdownHtml}
      </div>
    `;
  }

  private formatBreakdownDetails(
    breakdown?: ShieldOverviewBreakdownEntry,
    config?: { key: keyof ShieldOverviewBreakdownEntry; breakdownKeys: string[] },
  ): string {
    if (!breakdown || !config) return '';

    const featureBreakdown = breakdown[config.key];
    const items = config.breakdownKeys
      .map((key) => {
        const value = (featureBreakdown as Record<string, number>)[key] ?? 0;
        if (value === 0) return null;
        const formatted = this.namingScaleFormatPipe.transform(value, 'metricSymbol', this.unit, undefined, 2).formatted;
        return `${key.charAt(0).toUpperCase() + key.slice(1)}: ${formatted}`;
      })
      .filter(Boolean);

    const textColor = this.chartThemeVars?.generic?.tooltip?.textStyle?.color;
    return items.length > 0 ? `<div style="margin-left: 20px; font-size: 12px; color: ${textColor}">${items.join(' · ')}</div>` : '';
  }

  private findClosestBreakdown(rawTimestamp: number | string): ShieldOverviewBreakdownEntry | undefined {
    if (!this.breakdownData) return undefined;

    const targetMs = typeof rawTimestamp === 'number' ? rawTimestamp : new Date(rawTimestamp).getTime();
    const breakdownKeys = Object.keys(this.breakdownData);
    if (breakdownKeys.length === 0) return undefined;

    let closestKey = breakdownKeys[0];
    let closestDiff = Math.abs(new Date(closestKey).getTime() - targetMs);

    for (const key of breakdownKeys) {
      const diff = Math.abs(new Date(key).getTime() - targetMs);
      if (diff < closestDiff) {
        closestDiff = diff;
        closestKey = key;
      }
    }

    const maxDiff = this.dataGranularity() === 'daily' ? 86400000 : 3600000;
    return closestDiff <= maxDiff ? this.breakdownData[closestKey] : undefined;
  }

  private setSummaryLabels({
    ddosAttacks,
    WAFAttacks,
    RateLimitAttacks,
    botDetectionChallenged,
    accessListActions,
    uploadScanningBlocks,
    apiGuardianActions,
  }: StringNumberMap): void {
    this.chartSummaryLabels = [
      {
        ...MetricFormatPipe.metricFormat(WAFAttacks, this.unit),
        label: 'WAF',
        color: '#E1F2FF',
      },
      {
        ...MetricFormatPipe.metricFormat(ddosAttacks, this.unit),
        label: 'DDoS mitigation',
        color: this.chartThemeVars?.contextColors.info,
      },
      {
        ...MetricFormatPipe.metricFormat(RateLimitAttacks, this.unit),
        label: 'Rate limiting',
        color: this.chartThemeVars?.contextColors.success,
      },
      {
        ...MetricFormatPipe.metricFormat(botDetectionChallenged, this.unit),
        label: 'Bot detection',
        color: this.chartThemeVars?.contextColors.danger,
      },
      {
        ...MetricFormatPipe.metricFormat(accessListActions, this.unit),
        label: 'Access lists',
        color: this.chartThemeVars?.contextColors['light-danger'],
      },
      {
        ...MetricFormatPipe.metricFormat(uploadScanningBlocks, this.unit),
        label: 'Upload scanning',
        color: '#63A1FB',
      },
      {
        ...MetricFormatPipe.metricFormat(apiGuardianActions, this.unit),
        label: 'API Guardian',
        color: this.chartThemeVars?.contextColors.warning,
      },
    ];
  }
}
